import * as React from 'react';
import {styled, useTheme} from '@mui/material/styles';
import Box from '@mui/material/Box';
import MuiDrawer from '@mui/material/Drawer';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import {AppBar, DrawerHeader, LogoutButton} from "./UIElements";
import logo from '../img/Cryptolight_logo.png'
import emblem from "../img/emblem_logo.png";
import menuItems from "./data/menuItems";
import {ChevronLeftRounded} from "@mui/icons-material";
import {Outlet, useLocation, useNavigate} from 'react-router-dom';

const drawerWidth = 240;
const openedMixin = (theme) => ({
    width: drawerWidth,
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: 'hidden',
});

const closedMixin = (theme) => ({
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
    width: `calc(${theme.spacing(7)} + 1px)`,
    [theme.breakpoints.up('sm')]: {
        width: `calc(${theme.spacing(8)} + 1px)`,
    },
});

const Drawer = styled(MuiDrawer, {shouldForwardProp: (prop) => prop !== 'open'})(
    ({theme, open}) => ({
        width: drawerWidth,
        flexShrink: 0,
        whiteSpace: 'nowrap',
        boxSizing: 'border-box',
        backgroundColor: '#333333', // Background color for the drawer
        color: 'white',
        ...(open && {
            ...openedMixin(theme),
            '& .MuiDrawer-paper': {
                ...openedMixin(theme),
                backgroundColor: '#333333', // Background color for the drawer when open
                borderRight: '1px solid gray', // White frame when open
                color: 'whitesmoke'
            },
        }),
        ...(!open && {
            ...closedMixin(theme),
            '& .MuiDrawer-paper': {
                ...closedMixin(theme),
                backgroundColor: '#333333', // Background color for the drawer when closed
                borderRight: '1px solid gray', // White frame when closed
                color: 'whitesmoke'
            },
        }),
    }),
);

export default function Dashboard({setAccessToken}) {
    const navigate = useNavigate()
    const theme = useTheme();
    const [open, setOpen] = React.useState(false);
    const location = useLocation();
    const findMenuItemIndex = (path) => menuItems.findIndex((item) => item.path === path);
    const [selectedMenuItem, setSelectedMenuItem] = React.useState(findMenuItemIndex(location.pathname));

    const handleDrawerOpen = () => {
        setOpen(true);
    };
    const handleDrawerClose = () => {
        setOpen(false);
    };

    const handleMenuItemClick = (path, index) => {
        navigate(path); // Navigate to the clicked path
        setSelectedMenuItem(index); // Set the clicked menu item as selected
    };


    React.useEffect(() => {
        const currentPath = location.pathname;
        const index = findMenuItemIndex(currentPath);
        setSelectedMenuItem(index);
    }, [location]);

    return (
        <Box sx={{display: 'flex'}}>
            <AppBar position="fixed" open={open}>
                <Toolbar sx={{justifyContent: 'space-between'}}>
                    <div style={{display: 'flex', alignItems: 'center'}}>
                        <IconButton
                            color="inherit"
                            aria-label="open drawer"
                            onClick={handleDrawerOpen}

                            edge="start"
                            sx={{
                                marginRight: 5,
                                ...(open && {display: 'none'}),
                                width: '45px',
                                backgroundColor: 'white',
                                '&:hover': {
                                    backgroundColor: 'rgba(255, 255, 255, 0.2)', // White background on hover
                                },
                            }}
                        >
                            <img src={emblem} alt='Navigation' style={{width: '100%', height: 'auto'}}/>
                        </IconButton>
                        <img src={logo} alt={'Cryptolight'} style={{width: '20%'}}/>
                    </div>
                    <LogoutButton setAccessToken={setAccessToken}/>
                </Toolbar>
            </AppBar>
            <Drawer variant="permanent" open={open} onMouseEnter={handleDrawerOpen} onMouseLeave={handleDrawerClose}>
                <DrawerHeader>
                    <IconButton onClick={handleDrawerClose} style={{color: 'white'}}>
                        {theme.direction === 'rtl' ? <ChevronLeftRounded/> : <ChevronLeftRounded/>}
                    </IconButton>
                </DrawerHeader>
                <Divider/>
                <List>
                    {menuItems.map((item, index) => (
                        <ListItem key={item.text} disablePadding sx={{display: 'block'}}>
                            <ListItemButton
                                sx={{
                                    minHeight: 48,
                                    justifyContent: open ? 'initial' : 'center',
                                    px: 2.5,
                                }}
                                className={`${index === selectedMenuItem ? 'selectedMenuItem' : ''} ${'menuItemHover'}`}
                                onClick={() => handleMenuItemClick(item.path, index)}
                            >
                                <ListItemIcon
                                    sx={{
                                        minWidth: 0,
                                        mr: open ? 3 : 'auto',
                                        justifyContent: 'center',
                                        color: 'white'
                                    }}
                                >
                                    {/* Dynamically render the icon based on the icon name */}
                                    {<item.icon/>}
                                </ListItemIcon>
                                <ListItemText primary={item.text} sx={{opacity: open ? 1 : 0}}/>
                            </ListItemButton>
                        </ListItem>
                    ))}
                </List>
                <Divider/>
            </Drawer>
            <Box component="main" sx={{flexGrow: 1, p: 3}}>
                <DrawerHeader/>
                <Outlet/>
            </Box>
        </Box>
    );
}
